import { Component, Input, ViewChild, ElementRef, AfterViewInit, EventEmitter, Output, TemplateRef } from '@angular/core';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { DomHandler } from 'primeng/dom';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';
import { ButtonTypesEnum } from '../../enums/button-types.enum';
import { ActionKeys } from '../../enums/keys.enum';

/**
 * ### Accessibility Usage
 * + ***ariaDescribedBy*** - use this if the text area has an external error message, or it needs extra description for screen reader. Use message's id
 * + ***ariaHasPopup*** - use this if the text input comes with a click button, and clicking on it opens a new popup
 * + ***ariaInvalid*** - use this to indicate state if the email field can fail in certain scenario
 * + ***ariaLabel*** - use this to provide an accessible label for the text area. Not necessary if label or ariaLabelledBy is provided
 * + ***ariaLabelledBy*** - use this to provide an accessible label for the text input, only when the label is outside the input. It overwrites ariaLabel
 * + ***hint*** - use this to provide the input format to users
 * + ***iconClickableDisabledTooltip*** - use this if the text input comes with a click button, to provide reasons why the button is disabled
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***initialFocus*** - set to true to make this input have focus when it is created
 */
@Component({
    selector: 'agpr-text-input',
    templateUrl: './text-input.component.html',
    styleUrls: ['./text-input.component.scss'],
    providers: [getProvider(TextInputComponent)],
    standalone: false
})
export class TextInputComponent extends ControlValueAccessorModel<string> implements AfterViewInit {
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly buttonTypes = ButtonTypesEnum;

    @Input() public id: string;
    @Input() public isEditing: boolean = true;
    // For this component, we should input either a label or an ariaLabel, so that it is accessible.
    @Input() public label: string;
    @Input() public ariaLabel: string;
    @Input() public ariaInvalid: boolean = false;
    @Input() public ariaDescribedBy: string = '';
    @Input() public ariaLabelledBy: string = '';
    @Input() public ariaHasPopup: string = 'false';
    @Input() public placeholder: string;
    @Input() public tooltip: string;
    @Input() public tooltipPosition: string = 'right';
    @Input() public hint: string;
    @Input() public iconTooltip: string;
    @Input() public iconClickableTooltip: string;
    @Input() public iconClickableDisabledTooltip: string;
    @Input() public required: boolean = false;
    @Input() public disabled: boolean = false;
    @Input() public readonly: boolean = false;
    @Input() public icon: string;
    @Input() public iconColor: string;
    @Input() public maxLength: number;
    @Input() public autocomplete: string;
    @Input() public initialFocus: boolean = false;
    @Input() public initialHighlight: boolean = false;
    @Input() public bottomMargin: boolean = true;
    @Input() public styleClass: string = '';
    @Input() public type: string = 'text';
    @Input() public showClearButton: boolean = false;
    @Input() public iconPopoverTemplate: TemplateRef<any>;
    @Input() public iconPopoverStyleClass: string;
    // need to set this input to make SR announces popover when it receives focus
    @Input() public iconPopoverText: string;
    @Input() public iconClickable: boolean = false;
    @Input() public selectOnEdit: boolean = false;
    @Input() public tooltipLarge: boolean = false;
    @Input() public style: AgprComponentStyleEnum = AgprComponentStyleEnum.default;
    @Input() public isUrl = false;
    @Input() public emitFocusOut: boolean = false;
    @Input() public iconDecorative: boolean = false;
    // `change`, `keyup` and `keydown` are handled automatically by angular + browser
    @Output() blur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() focus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() keydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
    @Output() keyup: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();

    @Output() iconClick: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

    @ViewChild('input') input: ElementRef;

    zIndex: number;

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            if (this.initialFocus) {
                this.giveFocus();
            }
            if (this.initialHighlight) {
                this.giveHighlight();
            }
            this.zIndex = DomHandler.zindex;
        });
    }

    writeValue(value: string): void {
        this.value = value;
    }

    giveFocus() {
        if (this.input) {
            requestAnimationFrame(() => this.input.nativeElement.focus());
        }
    }

    giveHighlight() {
        if (this.input) {
            requestAnimationFrame(() => this.input.nativeElement.select());
        }
    }

    onFocus(event: any): void {
        if (this.selectOnEdit) {
            this.input.nativeElement.select();
        }

        this.focus.emit(event);
    }

    clearInput(): void {
        this.value = '';
    }

    onBlur(event: FocusEvent) {
        this.onModelTouched();
        this.blur.emit(event);
    }

    handleFocusOut(event: FocusEvent): void {
        if (!this.emitFocusOut) {
            event.stopPropagation();
        }
    }

    viewUrl(): void {
        window.open(this.value, '_blank');
    }

    onKeydown(event: KeyboardEvent) {
        if (event.key === ActionKeys.LEFT || event.key === ActionKeys.RIGHT) {
            event.stopImmediatePropagation();
            return;
        }
        this.keydown.emit(event);
    }

    onKeyup(event: KeyboardEvent) {
        if (event.key === ActionKeys.LEFT || event.key === ActionKeys.RIGHT) {
            event.stopImmediatePropagation();
            return;
        }
        this.keyup.emit(event);
    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }
}
